import { useCallback, useMemo, useState } from "react";

import { useTableViewPagination } from "@covertree/hooks";
import { ROWS_PER_PAGE } from "@covertree/consts";

import { useGetQuotesListItemsQuery } from "@hooks/policies/useGetQuotesListItems";

import {
  GetQuoteFilterByStatus,
  QuoteListItemsSortBy,
  SortOrder,
} from "@covertree/agent-portal-graphql";

import { useGetQuotesColumns } from "@hooks/policies/useGetQuotesColumns";
import { useMappedQuotes } from "@hooks/policies/useMappedQuotes";

const filters = [
  { label: GetQuoteFilterByStatus.Valid, value: GetQuoteFilterByStatus.Valid },
  { label: "Pending UW", value: GetQuoteFilterByStatus.Pending },
  {
    label: GetQuoteFilterByStatus.Expired,
    value: GetQuoteFilterByStatus.Expired,
  },
];

const initialPagination = {
  from: "0",
  filter: filters[0].value,
  order: SortOrder.Descending,
  sortBy: QuoteListItemsSortBy.UpdatedTimestamp,
};

export const useGetQuotes = () => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE);

  const {
    from,
    order,
    sortBy,
    filter,
    currentPage,
    onChangePage,
    onChangeFilter,
    onChangeSort,
  } = useTableViewPagination(0, rowsPerPage, initialPagination);

  const handleSortChange = useCallback(
    (nextSortBy: string) => () => {
      onChangeSort(nextSortBy);
    },
    [onChangeSort]
  );

  const queryVariables = useMemo(
    () => ({
      pagination: {
        from,
        size: rowsPerPage,
      },
      sorting: {
        sort: order,
        sortBy: sortBy as QuoteListItemsSortBy,
      },
      filter: {
        status: filter as GetQuoteFilterByStatus,
      },
    }),
    [filter, from, order, sortBy, rowsPerPage]
  );

  const {
    quotes: items,
    totalCount,
    loading,
    error,
  } = useGetQuotesListItemsQuery(queryVariables);

  const columns = useGetQuotesColumns();
  const quotes = useMappedQuotes(items, filter as GetQuoteFilterByStatus);

  return {
    quotes,
    loading,
    error: !!error,
    columns,
    filters,
    order,
    sortBy,
    currentPage,
    rowsPerPage,
    totalCount,
    handleSortChange,
    handleChangePage: onChangePage,
    handleFilterChange: onChangeFilter,
    handleRowsPerPageChange: setRowsPerPage,
  };
};
